import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'
import type {
  CoverageSummary, EffectivePolicyView, FaceRegistrationView, FaceSettingsView, FaceStatus,
  GeofenceAssignmentView, GeofencePolicyView, GeofenceScope, LocationFenceView, PunchReviewView,
  SaveGeofencePolicy, SelfPunchView,
} from '../types/geofence'

const BASE = '/v1/attendance/geofence'

export function useGeofenceLocations() {
  return useQuery({
    queryKey: ['geofence', 'locations'],
    queryFn: async () => (await apiClient.get<LocationFenceView[]>(`${BASE}/locations`)).data,
  })
}

export function useGeofencePolicies() {
  return useQuery({
    queryKey: ['geofence', 'policies'],
    queryFn: async () => (await apiClient.get<GeofencePolicyView[]>(`${BASE}/policies`)).data,
  })
}

export function useGeofenceAssignments() {
  return useQuery({
    queryKey: ['geofence', 'assignments'],
    queryFn: async () => (await apiClient.get<GeofenceAssignmentView[]>(`${BASE}/assignments`)).data,
  })
}

export function useGeofenceCoverage(enabled = true) {
  return useQuery({
    queryKey: ['geofence', 'coverage'],
    enabled,
    queryFn: async () => (await apiClient.get<CoverageSummary>(`${BASE}/coverage`)).data,
  })
}

export function useGeofencePunches(params: { from?: string; to?: string; result?: string[]; faceResult?: string[] }) {
  return useQuery({
    queryKey: ['geofence', 'punches', params],
    queryFn: async () =>
      (await apiClient.get<PunchReviewView[]>(`${BASE}/punches`, {
        params: {
          from: params.from || undefined,
          to: params.to || undefined,
          result: params.result?.length ? params.result.join(',') : undefined,
          faceResult: params.faceResult?.length ? params.faceResult.join(',') : undefined,
          limit: 500,
        },
      })).data,
    placeholderData: keepPreviousData,
  })
}

/**
 * Every write here can change who is able to punch, so every one refreshes the whole
 * geofence cache — the coverage screen most of all.
 */
export function useGeofenceMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['geofence'] })

  return {
    saveFence: useMutation({
      mutationFn: async ({ id, ...body }: {
        id: string; latitude: number | null; longitude: number | null; radiusMetres: number | null
      }) => (await apiClient.put<LocationFenceView>(`${BASE}/locations/${id}`, body)).data,
      onSuccess: invalidate,
    }),
    savePolicy: useMutation({
      mutationFn: async ({ id, ...body }: SaveGeofencePolicy & { id?: string }) =>
        id
          ? (await apiClient.put<GeofencePolicyView>(`${BASE}/policies/${id}`, body)).data
          : (await apiClient.post<GeofencePolicyView>(`${BASE}/policies`, body)).data,
      onSuccess: invalidate,
    }),
    deletePolicy: useMutation({
      mutationFn: async (id: string) => { await apiClient.delete(`${BASE}/policies/${id}`) },
      onSuccess: invalidate,
    }),
    assign: useMutation({
      mutationFn: async (body: { policyId: string; scopeType: GeofenceScope; scopeIds: string[] }) =>
        (await apiClient.post<GeofenceAssignmentView[]>(`${BASE}/assignments`, body)).data,
      onSuccess: invalidate,
    }),
    unassign: useMutation({
      mutationFn: async (id: string) => { await apiClient.delete(`${BASE}/assignments/${id}`) },
      onSuccess: invalidate,
    }),
  }
}

// --- self-service -----------------------------------------------------------

export function useMyGeofence() {
  return useQuery({
    queryKey: ['geofence', 'mine'],
    queryFn: async () =>
      (await apiClient.get<EffectivePolicyView>('/v1/attendance/my/geofence')).data,
  })
}

export function useMyPunches() {
  return useQuery({
    queryKey: ['attendance', 'my', 'punches'],
    queryFn: async () =>
      (await apiClient.get<SelfPunchView[]>('/v1/attendance/my/punches', { params: { limit: 20 } })).data,
  })
}

export interface SelfPunchInput {
  direction: 'IN' | 'OUT'
  latitude: number | null
  longitude: number | null
  accuracyMetres: number | null
  outsideReason?: string | null
  selfieFileId?: string | null
}

// --- face ---------------------------------------------------------------------

const FACE = '/v1/attendance/face'

export function useFaceSettings() {
  return useQuery({
    queryKey: ['geofence', 'face', 'settings'],
    queryFn: async () => (await apiClient.get<FaceSettingsView>(`${FACE}/settings`)).data,
  })
}

export function useFaceRegistrations() {
  return useQuery({
    queryKey: ['geofence', 'face', 'registrations'],
    queryFn: async () => (await apiClient.get<FaceRegistrationView[]>(`${FACE}/registrations`)).data,
  })
}

function photosForm(photos: Blob[], fields: Record<string, string>) {
  const form = new FormData()
  photos.forEach((photo, index) => form.append('photos', photo, `face-${index + 1}.jpg`))
  Object.entries(fields).forEach(([key, value]) => form.append(key, value))
  return form
}

export function useFaceMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['geofence'] })
  return {
    saveSettings: useMutation({
      mutationFn: async (body: { consentText: string; photoRetentionDays: number; deleteOnExit: boolean }) =>
        (await apiClient.put<FaceSettingsView>(`${FACE}/settings`, body)).data,
      onSuccess: invalidate,
    }),
    registerFor: useMutation({
      mutationFn: async ({ employeeId, photos }: { employeeId: string; photos: Blob[] }) =>
        (await apiClient.post<FaceStatus>(`${FACE}/employees/${employeeId}/register`,
          photosForm(photos, { consent: 'true' }))).data,
      onSuccess: invalidate,
    }),
    reset: useMutation({
      mutationFn: async ({ employeeId, reason }: { employeeId: string; reason: string }) => {
        await apiClient.post(`${FACE}/employees/${employeeId}/reset`, { reason })
      },
      onSuccess: invalidate,
    }),
  }
}

/** Fetches an image through the authenticated client and hands back an object URL. */
export async function fetchImage(url: string): Promise<string> {
  const response = await apiClient.get<Blob>(url, { responseType: 'blob' })
  return URL.createObjectURL(response.data)
}

export const facePhotoUrl = (employeeId: string, index: number) =>
  `${FACE}/employees/${employeeId}/photos/${index}`
export const punchPhotoUrl = (punchId: string) => `${BASE}/punches/${punchId}/photo`

export function useMyFace() {
  return useQuery({
    queryKey: ['geofence', 'face', 'mine'],
    queryFn: async () => (await apiClient.get<FaceStatus>('/v1/attendance/my/face')).data,
  })
}

export function useRegisterMyFace() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (photos: Blob[]) =>
      (await apiClient.post<FaceStatus>('/v1/attendance/my/face', photosForm(photos, { consent: 'true' }))).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['geofence'] }),
  })
}

export async function uploadPunchPhoto(photo: Blob): Promise<string> {
  const form = new FormData()
  form.append('photo', photo, 'punch.jpg')
  return (await apiClient.post<{ fileId: string }>('/v1/attendance/my/punch-photo', form)).data.fileId
}

export function usePunch() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: SelfPunchInput) =>
      (await apiClient.post<SelfPunchView>('/v1/attendance/my/punch', body)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['attendance', 'my', 'punches'] }),
  })
}
